


















import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import MultiLevelObject from "@/interfaces/MultiLevelObject";
import MultiLevelSelectV2, {
  MultiLevelMultipleObject,
} from "@/components/MultiLevelSelectV2.vue";
import { Getter } from "vuex-class";
import TaxonomyValue from "@/interfaces/TaxonomyValue";
import Taxonomy from "@/interfaces/Taxonomy";
import { TAXONOMY_ICON_SET } from "@/components/TaxonomyIcon.vue";

import { ProductSector, User, ProductCategory, ProductClass } from "@/models";

enum Levels {
  SECTOR,
  CATEGORY,
  CLASS,
}

interface SingleValueStruct {
  sector: string | undefined;
  category: string | undefined;
  class: string | undefined;
}

export interface UserTaxonomies {
  [key: number]: Set<string>;
}

@Component({
  name: "TaxonomySelectV2",
  components: {
    MultiLevelSelectV2,
  },
})
export default class TaxonomySelectV2 extends Vue {
  readonly TAXONOMY_ICON_SET = TAXONOMY_ICON_SET;
  @Prop() readonly value!: TaxonomyValue;
  @Prop({ default: false }) readonly multiple?: boolean;
  @Prop({ default: 0 }) readonly selectableFromLevel?: number;

  @Getter("locked") readonly locked!: boolean;
  @Getter("taxonomies") readonly taxonomies!: Array<Taxonomy>;
  @Getter("user") readonly user!: User | undefined;
  @Prop({ type: Boolean, default: false }) readonly searchEnabled!: boolean;

  @Watch("value", { deep: true, immediate: true })
  onValueChange(val: TaxonomyValue | null | undefined) {
    if (!val) {
      this.selected = this.multiple ? [] : null;
      return;
    }

    this.selected = this.multiple
      ? this.parseMultiple(val)
      : this.parseSingle(val);
  }

  headers = ["Settore", "Categoria", "Classe"];
  selected: string | Array<string[]> | null = null;

  get levelContext() {
    if (this.multiple) {
      return Levels.SECTOR;
    }
    if (this.value.class) {
      return Levels.CLASS;
    } else if (this.value.category) {
      return Levels.CATEGORY;
    } else {
      return Levels.SECTOR;
    }
  }

  get options() {
    if (this.taxonomies == undefined) {
      return [];
    }

    const options = this.taxonomies.map((sector) => {
      return {
        id: sector.id,
        text: sector.name,
        denyMultiselect: !this.canMultiselect(Levels.SECTOR),
        children: sector.categories
          ? sector.categories.map((category) => {
              return {
                id: category.id,
                text: category.name,
                denyMultiselect: !this.canMultiselect(Levels.CATEGORY),
                locked: this.isLocked(category, Levels.CATEGORY),
                children: category.classes
                  ? category.classes.map((klass) => {
                      return {
                        id: klass.id,
                        text: klass.name,
                        locked: this.isLocked(klass, Levels.CLASS),
                      };
                    })
                  : [],
              };
            })
          : [],
      };
    });

    return options;
  }

  get taxonomiesMap(): {
    categories: Map<string, { sector: string; classes: string[] }>;
    classes: Map<string, { sector: string; category: string }>;
  } {
    const taxonomiesMap = {
      categories: new Map(),
      classes: new Map(),
    };

    this.taxonomies.forEach((sector: Taxonomy) => {
      sector.categories?.forEach((category: Taxonomy) => {
        taxonomiesMap.categories.set(category.id, {
          sector: sector.id,
          classes: category.classes?.map((e) => e.id) || [],
        });

        category.classes?.forEach((klass: Taxonomy) => {
          taxonomiesMap.classes.set(klass.id, {
            sector: sector.id,
            category: category.id,
          });
        });
      });
    });

    return taxonomiesMap;
  }

  get userTaxonomies(): UserTaxonomies | undefined {
    const userTaxonomies = {
      0: new Set<string>(),
      1: new Set<string>(),
      2: new Set<string>(),
    };
    if (!this.user) {
      return undefined;
    }

    if (
      !this.user.profile?.taxonomies ||
      !this.user.profile?.taxonomies.length
    ) {
      return undefined;
    }

    this.user.profile.taxonomies.forEach((taxonomy: Taxonomy) => {
      if (taxonomy instanceof ProductSector) {
        userTaxonomies[Levels.SECTOR].add(taxonomy.id);
      } else if (taxonomy instanceof ProductCategory) {
        userTaxonomies[Levels.CATEGORY].add(taxonomy.id);
      } else if (taxonomy instanceof ProductClass) {
        userTaxonomies[Levels.CLASS].add(taxonomy.id);
      }
    });
    return userTaxonomies;
  }

  parseMultiple(val: TaxonomyValue): string[][] {
    const selected: string[][] = [];

    selected[Levels.SECTOR] = this.toMultiple(val.sector);
    selected[Levels.CATEGORY] = this.toMultiple(val.category);
    selected[Levels.CLASS] = this.toMultiple(val.class);

    return selected;
  }

  parseSingle(val: TaxonomyValue): string | null {
    if (val.class !== undefined) return this.toSingle(val.class);
    if (val.category !== undefined) return this.toSingle(val.category);
    if (val.sector !== undefined) return this.toSingle(val.sector);
    return null;
  }

  toMultiple(val: string | string[] | undefined): string[] {
    if (!val) return [];
    return Array.isArray(val) ? val : [val];
  }

  toSingle(val: string | string[] | undefined): string | null {
    if (!val) return null;
    return Array.isArray(val) ? val[0] : val;
  }

  isLocked(element: Taxonomy, level: 1 | 2) {
    if (this.userTaxonomies == undefined || this.user?.isAdmin) {
      return false;
    }
    const userTaxonomies = this.userTaxonomies;

    if (level == Levels.CLASS) {
      const klass = this.taxonomiesMap.classes.get(element.id);

      if (klass) {
        return (
          userTaxonomies[Levels.CLASS].has(element.id) == false &&
          userTaxonomies[Levels.CATEGORY].has(klass.category) == false &&
          userTaxonomies[Levels.SECTOR].has(klass.sector) == false
        );
      }
    } else if (level == Levels.CATEGORY) {
      const category = this.taxonomiesMap.categories.get(element.id);

      if (category) {
        return (
          userTaxonomies[Levels.SECTOR].has(category.sector) == false &&
          userTaxonomies[Levels.CATEGORY].has(element.id) == false &&
          category.classes.every(
            (e) => userTaxonomies[Levels.CLASS].has(e) == false
          )
        );
      }
    }

    return this.userTaxonomies[level].has(element.id) == false;
  }

  onSelected(
    selected: MultiLevelObject | MultiLevelMultipleObject,
    level: number
  ) {
    if (this.multiple) {
      return;
    }

    const value: SingleValueStruct = {
      sector: undefined,
      category: undefined,
      class: undefined,
    };

    switch (level) {
      case Levels.SECTOR:
        value.sector = (selected as MultiLevelObject).id;
        break;
      case Levels.CATEGORY:
        value.category = (selected as MultiLevelObject).id;
        break;
      case Levels.CLASS:
        value.class = (selected as MultiLevelObject).id;
        break;
      default:
        break;
    }

    this.$emit("input", value);
  }

  onInput(input: string | Array<string[]> | null) {
    if (this.multiple == false) {
      if (input === null || input === undefined) {
        this.$emit("input", {
          sector: undefined,
          category: undefined,
          class: undefined,
        });
      }
      return;
    }

    if (!input || !Array.isArray(input)) {
      this.$emit("input", { sector: [], category: [], class: [] });
      return;
    }

    const inputArray: string[][] = input;

    const value: TaxonomyValue = {
      sector: inputArray[Levels.SECTOR],
      category: inputArray[Levels.CATEGORY],
      class: inputArray[Levels.CLASS],
    };

    this.$emit("input", value);
  }

  canMultiselect(level: number) {
    return level >= (this.selectableFromLevel ?? 0);
  }
}
